import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Bookmark, Trash2, TrendingUp, TrendingDown, Plus } from 'lucide-react'
import { api } from '@/lib/api'
import StockSearch from '@/components/trading/StockSearch'
import { formatCurrency, formatPercent, cn } from '@/lib/utils'
import type { WatchlistItem, SearchResult } from '@/types'

export default function Watchlist() {
  const [items, setItems] = useState<WatchlistItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [removing, setRemoving] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const load = async () => {
    setIsLoading(true)
    const res = await api.getWatchlist()
    if (res.success && res.data) setItems(res.data)
    setIsLoading(false)
  }

  useEffect(() => { load() }, [])

  const handleAdd = async (result: SearchResult) => {
    setAdding(true)
    const res = await api.addToWatchlist(result.symbol)
    setAdding(false)

    if (res.success) {
      load()
    }
  }

  const handleRemove = async (symbol: string) => {
    setRemoving(symbol)
    await api.removeFromWatchlist(symbol)
    setItems((prev) => prev.filter((i) => i.symbol !== symbol))
    setRemoving(null)
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold">Watchlist</h1>
        <p className="text-white/40 text-sm mt-0.5">Stocks you're tracking</p>
      </div>

      {/* Add to watchlist */}
      <div className="card p-5">
        <h3 className="text-sm font-medium mb-3 text-white/60">Add to watchlist</h3>
        <div className="flex gap-3 items-start">
          <div className="flex-1 max-w-sm">
            <StockSearch
              onSelect={handleAdd}
              placeholder="Search a stock to watch..."
            />
          </div>
          {adding && (
            <div className="w-8 h-8 flex items-center justify-center">
              <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
      </div>

      {/* Watchlist */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-20 bg-white/[0.04] rounded-xl animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="card p-12 text-center">
          <Bookmark className="w-10 h-10 text-white/15 mx-auto mb-3" />
          <div className="text-white/30 text-sm">Your watchlist is empty</div>
          <div className="text-white/20 text-xs mt-1">Search above to add stocks</div>
        </div>
      ) : (
        <div className="card divide-y divide-white/[0.04]">
          {items.map((item) => {
            const quote = item.quote
            const isPositive = (quote?.changePercent ?? 0) >= 0

            return (
              <div key={item.symbol} className="flex items-center gap-4 px-5 py-4">
                <div className="w-10 h-10 bg-white/[0.06] rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-white/60">{item.symbol.slice(0, 2)}</span>
                </div>

                <div className="flex-1 min-w-0">
                  <Link
                    to={`/trade/${item.symbol}`}
                    className="font-semibold text-sm hover:text-accent transition-colors"
                  >
                    {item.symbol}
                  </Link>
                  {quote && (
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="font-mono text-sm">{formatCurrency(quote.price)}</span>
                      <span className={cn('text-xs flex items-center gap-0.5', isPositive ? 'text-gain' : 'text-loss')}>
                        {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {formatPercent(quote.changePercent)}
                      </span>
                    </div>
                  )}
                  {!quote && (
                    <div className="text-xs text-white/20 mt-0.5">Price unavailable</div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Link
                    to={`/trade/${item.symbol}`}
                    className="flex items-center gap-1 text-xs text-accent hover:underline"
                  >
                    <Plus className="w-3 h-3" />
                    Trade
                  </Link>
                  <button
                    onClick={() => handleRemove(item.symbol)}
                    disabled={removing === item.symbol}
                    className="p-1.5 text-white/20 hover:text-loss transition-colors rounded-lg hover:bg-loss/10 disabled:opacity-40"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
